import { useEffect, useMemo, useRef, useState } from 'react';
import type { RelationshipPair } from '../../../core/entities/graph-document';
import type { JourneyNode, NodeType } from '../../../core/entities/node';
import {
  canonicalRelationshipPairId,
  normalizeExplicitRelationshipPair,
  validateExplicitRelationshipPair,
  type ExplicitRelationshipPairInput,
} from '../../../core/policies/explicit-relationships';
import { useLocale } from '../../preferences/i18n/LocaleProvider';
import { RelationshipLabelEditor } from './RelationshipLabelEditor';

type RelationshipTargetTypeFilter = NodeType | 'All';

export function RelationshipPanel({
  sourceNode,
  nodes,
  relationships,
  onSave,
  onDelete,
  onClose,
  initialTargetNodeId = null,
  readonly = false,
}: {
  sourceNode: JourneyNode;
  nodes: JourneyNode[];
  relationships: RelationshipPair[];
  onSave(input: ExplicitRelationshipPairInput): void;
  onDelete?(firstNodeId: string, secondNodeId: string): void;
  onClose: () => void;
  initialTargetNodeId?: string | null;
  readonly?: boolean;
}) {
  const { locale, t } = useLocale();
  const targetNodes = useMemo(() => {
    const collator = new Intl.Collator(locale, {
      numeric: true,
      sensitivity: 'base',
      usage: 'sort',
    });

    return nodes
      .filter((node) => node.nodeId !== sourceNode.nodeId)
      .sort((left, right) => (
        collator.compare(displayNameForSort(left), displayNameForSort(right))
        || collator.compare(left.nodeId, right.nodeId)
      ));
  }, [locale, nodes, sourceNode.nodeId]);
  const [targetTypeFilter, setTargetTypeFilter] = useState<RelationshipTargetTypeFilter>('All');
  const filteredTargetNodes = useMemo(
    () => targetTypeFilter === 'All' ? targetNodes : targetNodes.filter((node) => node.type === targetTypeFilter),
    [targetNodes, targetTypeFilter],
  );
  const initialTargetRef = useRef<{ sourceNodeId: string; targetNodeId: string | null }>({
    sourceNodeId: sourceNode.nodeId,
    targetNodeId: initialTargetNodeId,
  });
  const [targetNodeId, setTargetNodeId] = useState(() => (
    initialTargetNodeId && initialTargetNodeId !== sourceNode.nodeId ? initialTargetNodeId : ''
  ));
  const [sourceToTargetText, setSourceToTargetText] = useState('');
  const [targetToSourceText, setTargetToSourceText] = useState('');

  const targetNode = targetNodes.find((node) => node.nodeId === targetNodeId) ?? null;
  const selectedPair = useMemo(
    () => (targetNodeId ? relationships.find((pair) => pair.pairId === canonicalRelationshipPairId(sourceNode.nodeId, targetNodeId)) ?? null : null),
    [relationships, sourceNode.nodeId, targetNodeId],
  );

  useEffect(() => {
    const previousInitialTarget = initialTargetRef.current;
    if (previousInitialTarget.sourceNodeId === sourceNode.nodeId && previousInitialTarget.targetNodeId === initialTargetNodeId) {
      return;
    }

    initialTargetRef.current = {
      sourceNodeId: sourceNode.nodeId,
      targetNodeId: initialTargetNodeId,
    };
    setTargetNodeId(initialTargetNodeId && initialTargetNodeId !== sourceNode.nodeId ? initialTargetNodeId : '');
  }, [initialTargetNodeId, sourceNode.nodeId]);

  useEffect(() => {
    if (targetNodeId && !filteredTargetNodes.some((node) => node.nodeId === targetNodeId)) {
      setTargetNodeId('');
    }
  }, [filteredTargetNodes, targetNodeId]);

  useEffect(() => {
    if (!targetNodeId) {
      setSourceToTargetText('');
      setTargetToSourceText('');
      return;
    }

    if (!selectedPair) {
      setSourceToTargetText('');
      setTargetToSourceText('');
      return;
    }

    const sourceToTarget = selectedPair.directions.find(
      (direction) => direction.sourceNodeId === sourceNode.nodeId && direction.targetNodeId === targetNodeId,
    );
    const targetToSource = selectedPair.directions.find(
      (direction) => direction.sourceNodeId === targetNodeId && direction.targetNodeId === sourceNode.nodeId,
    );

    setSourceToTargetText(sourceToTarget?.labels.join(', ') ?? '');
    setTargetToSourceText(targetToSource?.labels.join(', ') ?? '');
  }, [selectedPair, sourceNode.nodeId, targetNodeId]);

  const validation = useMemo(() => {
    if (!targetNodeId) {
      return { valid: false, errors: [] };
    }

    return validateExplicitRelationshipPair(
      normalizeExplicitRelationshipPair({
        sourceNodeId: sourceNode.nodeId,
        targetNodeId,
        sourceToTargetLabels: splitLabels(sourceToTargetText),
        targetToSourceLabels: splitLabels(targetToSourceText),
      }),
      nodes.map((node) => node.nodeId),
    );
  }, [nodes, sourceNode.nodeId, sourceToTargetText, targetNodeId, targetToSourceText]);

  return (
    <aside
      aria-label={t('relationshipPanelTitle')}
      className="floating-panel"
      style={{ width: 'min(560px, 92vw)', maxHeight: 'calc(100vh - 64px)', overflowY: 'auto', padding: 20 }}
    >
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(180, 223, 255, 0.84)' }}>
              {t('relationshipPanelEyebrow')}
            </span>
            <h2 style={{ margin: 0, fontSize: 28, lineHeight: 1.05 }}>{sourceNode.displayName}</h2>
          </div>
          <button className="hud-subtle-button" onClick={onClose} type="button">
            {t('close')}
          </button>
        </div>

        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))' }}>
          <label style={{ display: 'grid', gap: 8 }}>
            <span style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(180, 223, 255, 0.84)' }}>
              {t('relationshipTargetTypeFilter')}
            </span>
            <select
              aria-label={t('relationshipTargetTypeFilter')}
              onChange={(event) => setTargetTypeFilter(event.target.value as RelationshipTargetTypeFilter)}
              style={selectStyle}
              value={targetTypeFilter}
            >
              <option value="All">{t('all')}</option>
              <option value="Human">{t('human')}</option>
              <option value="Project">{t('project')}</option>
              <option value="Occupation">{t('occupation')}</option>
            </select>
          </label>

          <label style={{ display: 'grid', gap: 8 }}>
            <span style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(180, 223, 255, 0.84)' }}>
              {t('relationshipTargetNode')}
            </span>
            <select
              aria-label={t('relationshipTargetNode')}
              disabled={filteredTargetNodes.length === 0}
              onChange={(event) => setTargetNodeId(event.target.value)}
              style={selectStyle}
              value={targetNodeId}
            >
              <option value="">{t('relationshipTargetPlaceholder')}</option>
              {filteredTargetNodes.map((node) => (
                <option key={node.nodeId} value={node.nodeId}>
                  {node.displayName}
                </option>
              ))}
            </select>
          </label>
        </div>

        {!targetNode ? (
          <p style={{ margin: 0, color: 'rgba(239, 246, 255, 0.72)' }}>{t('relationshipPanelEmptyState')}</p>
        ) : (
          <>
            <RelationshipLabelEditor
              label={`${sourceNode.displayName} -> ${targetNode.displayName}`}
              onChange={setSourceToTargetText}
              readonly={readonly}
              value={sourceToTargetText}
            />
            <RelationshipLabelEditor
              label={`${targetNode.displayName} -> ${sourceNode.displayName}`}
              onChange={setTargetToSourceText}
              readonly={readonly}
              value={targetToSourceText}
            />
            {validation.errors.length > 0 ? (
              <div role="alert" style={{ display: 'grid', gap: 8, padding: 14, borderRadius: 18, background: 'rgba(58, 13, 22, 0.42)', border: '1px solid rgba(255, 180, 192, 0.18)' }}>
                <strong>{t('relationshipBlockedTitle')}</strong>
                <p style={{ margin: 0, color: 'rgba(255, 214, 220, 0.84)' }}>{t('relationshipBlockedHint')}</p>
                <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6, color: '#ffb4c0' }}>
                  {validation.errors.map((error) => (
                    <li key={error}>{relationshipErrorMessage(error, t)}</li>
                  ))}
                </ul>
              </div>
            ) : selectedPair ? (
              <p style={{ margin: 0, color: 'rgba(239, 246, 255, 0.72)' }}>{t('relationshipPanelExistingPair')}</p>
            ) : (
              <p style={{ margin: 0, color: 'rgba(239, 246, 255, 0.72)' }}>{t('relationshipPanelCreateGuidance')}</p>
            )}
          </>
        )}

        {readonly ? <p style={{ margin: 0, color: 'rgba(255, 214, 143, 0.92)' }}>{t('relationshipReadonlyNotice')}</p> : null}

        <div className="hud-toolbar" style={{ justifyContent: 'flex-end' }}>
          {selectedPair && onDelete ? (
            <button className="hud-subtle-button" disabled={readonly} onClick={() => onDelete(sourceNode.nodeId, targetNodeId)} type="button">
              {t('relationshipDeleteAction')}
            </button>
          ) : null}
          <button className="hud-subtle-button" disabled={readonly || !targetNodeId || !validation.valid} onClick={() => onSave({
            sourceNodeId: sourceNode.nodeId,
            targetNodeId,
            sourceToTargetLabels: splitLabels(sourceToTargetText),
            targetToSourceLabels: splitLabels(targetToSourceText),
          })} type="button">
            {t('relationshipSaveAction')}
          </button>
        </div>
      </div>
    </aside>
  );
}

const selectStyle = {
  borderRadius: 16,
  border: '1px solid rgba(180, 223, 255, 0.18)',
  background: 'rgba(3, 12, 28, 0.52)',
  color: 'rgba(239, 246, 255, 0.92)',
  padding: '12px 14px',
  font: 'inherit',
};

function splitLabels(value: string): string[] {
  return value
    .split(',')
    .map((label) => label.trim())
    .filter((label) => label.length > 0);
}

function relationshipErrorMessage(error: string, t: ReturnType<typeof useLocale>['t']): string {
  if (error === 'at least one relationship label is required') {
    return t('relationshipValidationEmpty');
  }

  if (error === 'duplicate relationship labels are not allowed within the same direction') {
    return t('relationshipValidationDuplicate');
  }

  if (error === 'relationship pair references a missing node') {
    return t('relationshipValidationMissingTarget');
  }

  if (error === 'self relationships are not allowed') {
    return t('relationshipValidationSelf');
  }

  return error;
}

function displayNameForSort(node: JourneyNode): string {
  const displayName = node.displayName.trim();
  return displayName || node.nodeId;
}
